import React from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  useTheme,
  alpha,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  LocalShipping as TruckIcon,
  Route as RouteIcon,
  AssignmentTurnedIn as TaskIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  Assessment as ReportIcon,
  Verified as VerifiedIcon,
  Timeline as TimelineIcon,
  NotificationsActive as NotifyIcon,
  CloudSync as SyncIcon,
  CheckCircle as CheckIcon,
  PhoneIphone as MobileIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// Image imports (replace with your own)
import transportDashboard from "./Images/TransportDashboard.png";
import transportTrack from "./Images/TransportTrack.png";
import transportMobile from "./Images/TransportMobile.png";

const TransportManagement = () => {
  const theme = useTheme();

  const PremiumCard = styled(Card)(({ theme }) => ({
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.05
    )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    borderRadius: 20,
    padding: theme.spacing(3),
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    },
  }));

  const features = [
    {
      title: "Fleet Tracking",
      description: "Track all vehicles in real time with live GPS integration.",
      icon: <LocationIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.primary.main,
    },
    {
      title: "Driver Management",
      description: "Assign, monitor, and rate drivers for every route.",
      icon: <PeopleIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.success.main,
    },
    {
      title: "Route Optimization",
      description: "Get smart, fuel-efficient routes and reduce travel costs.",
      icon: <RouteIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.info.main,
    },
    {
      title: "Automatic Trip Logs",
      description: "Auto-generate trip details, distance, and time reports.",
      icon: <TimelineIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.warning.main,
    },
    {
      title: "Instant Notifications",
      description:
        "Receive alerts for delays, breakdowns, and route deviations.",
      icon: <NotifyIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.error.main,
    },
    {
      title: "Performance Reports",
      description: "Analyze trip efficiency and fuel usage with one click.",
      icon: <ReportIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.secondary.main,
    },
  ];

  return (
    <Box
      sx={{
        pb: 8,
        background: "linear-gradient(135deg, #f8faff 0%, #ffffff 100%)",
      }}
    >
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.08
          )} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={6}
            alignItems="center"
            direction={{ xs: "column-reverse", md: "row" }}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <Chip
                icon={<TruckIcon />}
                label="Transport Management System"
                color="primary"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                }}
              >
                Smart Transport & Fleet Automation
              </Typography>

              <Typography variant="h5" color="text.secondary" sx={{ mb: 4 }}>
                Manage vehicles, drivers, routes, and trips — all in one
                connected dashboard. Boost efficiency and reduce operational
                costs.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<TruckIcon />}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 700,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  }}
                >
                  Start Managing Fleet
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{ px: 4, py: 1.5, fontWeight: 600 }}
                >
                  Watch Demo
                </Button>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                component="img"
                src={transportDashboard}
                alt="Transport Dashboard"
                sx={{
                  width: "100%",
                  borderRadius: 4,
                  boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" sx={{ mb: 6 }}>
          <Typography variant="h3" fontWeight={700} sx={{ mb: 1 }}>
            Everything You Need for{" "}
            <Box component="span" color="primary.main">
              Transport Management
            </Box>
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mx: "auto" }}>
            Streamline logistics, monitor operations, and improve delivery
            performance with automation.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid
              size={{ xs: 12, sm: 6, md: 4 }}
              xs={12}
              sm={6}
              md={4}
              key={index}
            >
              <PremiumCard>
                <CardContent sx={{ textAlign: "center" }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(feature.color, 0.1),
                      color: feature.color,
                      width: 70,
                      height: 70,
                      mb: 3,
                      mx: "auto",
                      border: `2px solid ${alpha(feature.color, 0.2)}`,
                    }}
                  >
                    {feature.icon}
                  </Avatar>
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </PremiumCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Integration Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <PremiumCard>
          <Grid
            container
            spacing={6}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h3" fontWeight={700} sx={{ mb: 1 }}>
                <span style={{ color: "#1976d2" }}>Fully Integrated</span> with
                E-Way & Accounting
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                Auto-sync transport data with e-way bills, invoices, and
                delivery challans.
              </Typography>
              <List>
                {[
                  "Auto link vehicle and driver details",
                  "Sync trip logs with accounting",
                  "Track delivery status in real-time",
                  "Generate transport invoices automatically",
                ].map((item, index) => (
                  <ListItem key={index} sx={{ px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <CheckIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{
                        variant: "h6",
                        fontWeight: 500,
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                component="img"
                src={transportTrack}
                alt="Transport Tracking"
                sx={{
                  width: "100%",
                  borderRadius: 4,
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                }}
              />
            </Grid>
          </Grid>
        </PremiumCard>
      </Container>

      {/* Mobile Transport Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              component="img"
              src={transportMobile}
              alt="Transport Mobile App"
              sx={{
                width: "100%",
                borderRadius: 4,
                boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h3" fontWeight={700} sx={{ mb: 1 }}>
              Manage Trips{" "}
              <span style={{ color: "#1976d2" }}>Anywhere, Anytime</span>
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 5 }}>
              Assign drivers, track shipments, and update delivery status — all
              from your mobile.
            </Typography>
            <Stack spacing={2}>
              {[
                "Instant trip assignment",
                "Offline trip updates",
                "Live vehicle location",
                "Route deviation alerts",
                "Digital proof of delivery",
              ].map((item, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                  <MobileIcon color="primary" sx={{ mr: 2 }} />
                  <Typography variant="h6" fontWeight={500}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* Final CTA */}
      <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
        <PremiumCard>
          <Typography variant="h3" fontWeight={700} sx={{ mb: 1 }}>
            Ready to{" "}
            <span style={{ color: "#1976d2" }}>Automate Your Transport</span>{" "}
            Operations?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
            Streamline your fleet, drivers, and logistics — save time and fuel
            with automation.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 5,
                py: 1.5,
                fontWeight: 700,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              }}
              startIcon={<TruckIcon />}
            >
              Start Transport Management
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{ px: 5, py: 1.5, fontWeight: 600 }}
            >
              Schedule Demo
            </Button>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            ✓ Real-time GPS • ✓ Driver Tracking • ✓ Route Optimization
          </Typography>
        </PremiumCard>
      </Container>
    </Box>
  );
};

export default TransportManagement;
